import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Item,
  ItemContent,
  ItemTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@solinkcorp/solink-ui';
import get from 'lodash/get';
import { EllipsisVertical } from 'lucide-react';
import moment from 'moment';
import useLocale from 'src/hooks/useLocale';
import useTabName from 'src/hooks/useTabName';
import { NOTIFICATIONS_FILTER_BY_STATE, TAB_VIDEO } from 'vd-constants';
import { notificationFilterIcon } from './notificationFilterIcons';

const NotificationItem = (props) => {
  const {
    data,
    darkTheme,
    disableArchive,
    disableMarkAsRead,
    hasError,
    index,
    isChecked,
    isNew,
    isRead,
    onArchive,
    onCheck,
    onMarkAsRead,
    onView,
  } = props;

  const locale = useLocale();
  const { localeCode } = locale;
  const tabName = useTabName();

  const handleOnArchive = () => onArchive(index);
  const handleOnMarkAsRead = () => onMarkAsRead(index);
  const handleViewClick = () => {
    const isVideoPage = tabName === TAB_VIDEO;
    onView(index, isVideoPage);
  };

  const { created, body, report: { name } = {}, state, title, type } = data;
  const reportName = get(name, localeCode, get(name, 'en-CA'));
  const unread = !isRead && state === NOTIFICATIONS_FILTER_BY_STATE.unread;
  const TypeIcon = notificationFilterIcon(type);
  const typeLabel = locale.notifications[String(type || '').toLowerCase()];
  const showMarkAsRead = !disableMarkAsRead;
  const showArchive = !disableArchive;
  const showMenu = showMarkAsRead || showArchive;

  return (
    <Item
      variant="outline"
      size="sm"
      data-index={index}
      className={cn(
        'tw items-start gap-3 py-3 transition-colors hover:border-border-strong hover:bg-background-elevated',
        hasError && 'border-destructive',
        isNew && 'notification-arrive',
      )}
    >
      {!disableArchive && (
        <Checkbox
          checked={!!isChecked}
          className="tw mt-0.5"
          onCheckedChange={(checked) => onCheck({ value: checked === true, index })}
          aria-label={title}
        />
      )}
      <ItemContent className="tw min-w-0 flex-1 gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="tw min-w-0 flex-1 cursor-pointer bg-transparent p-0 text-left"
              onClick={handleViewClick}
            >
              <div className="tw flex min-w-0 items-center gap-2">
                <span
                  className="tw inline-flex shrink-0"
                  {...(typeLabel
                    ? { role: 'img', 'aria-label': typeLabel }
                    : { 'aria-hidden': true })}
                >
                  <TypeIcon className="tw size-4 text-foreground-secondary" aria-hidden />
                </span>
                <ItemTitle
                  className={cn(
                    'tw min-w-0 truncate',
                    unread
                      ? 'font-semibold text-foreground-primary'
                      : 'font-normal text-foreground-secondary',
                  )}
                >
                  {title}
                </ItemTitle>
              </div>
              <div className="text-foreground-tertiary tw text-xs">
                {moment(created).format('MMM D, YYYY HH:mm:ss')}
              </div>
              {body && (
                <div className="tw break-words text-sm text-foreground-secondary">{body}</div>
              )}
              {reportName && (
                <div className="tw text-sm text-foreground-secondary">
                  {locale.notifications.generatedBy}: {reportName}
                </div>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={12}
            variant={darkTheme ? 'dark' : 'auto'}
            className="z-[1001]"
          >
            {locale.notifications.view}
          </TooltipContent>
        </Tooltip>
      </ItemContent>
      {showMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              // Ghost hover uses `--accent`, which matches this row's `hover:bg-background-elevated`.
              // Hovering the button also hovers the row, so step the button to `background-overlay`
              // (same pattern as BrowseCard). `dark:` overrides ghost's `dark:hover:bg-accent/50`.
              className="tw shrink-0 hover:bg-background-overlay dark:hover:bg-background-overlay"
            >
              <EllipsisVertical />
              <span className="sr-only">{locale.general.actions}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            variant={darkTheme ? 'dark' : 'light'}
            align="end"
            className="z-[1001]"
          >
            {showMarkAsRead && (
              <DropdownMenuItem onClick={handleOnMarkAsRead}>
                {locale.notifications.markAsRead}
              </DropdownMenuItem>
            )}
            {showArchive && (
              <DropdownMenuItem onClick={handleOnArchive}>
                {locale.notifications.archive}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </Item>
  );
};

export default NotificationItem;
